import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { logger } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function usePushNotifications(deviceId) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      // Fetch VAPID public key
      axios.get(`${API}/notifications/vapid-public-key`)
        .then(res => setVapidPublicKey(res.data.public_key))
        .catch(err => logger.error('Failed to fetch VAPID key:', err));
    }
  }, []);

  // Check current subscription status
  useEffect(() => {
    if (!isSupported) return;
    
    navigator.serviceWorker.ready.then(registration => {
      registration.pushManager.getSubscription().then(subscription => {
        setIsSubscribed(!!subscription);
      });
    });
  }, [isSupported]);

  // Fetch pending notifications (unread count for badge)
  const fetchPendingNotifications = useCallback(async () => {
    if (!deviceId) return;
    
    try {
      // Fetch unread count for badge
      const pendingRes = await axios.get(`${API}/notifications/pending/${deviceId}`);
      setPendingNotifications(pendingRes.data.notifications || []);
      setNotificationCount(pendingRes.data.count || 0);
      
      // Fetch all notifications (read + unread) for display
      const historyRes = await axios.get(`${API}/notifications/history/${deviceId}`);
      setAllNotifications(historyRes.data.notifications || []);
    } catch (error) {
      logger.error('Failed to fetch notifications:', error);
    }
  }, [deviceId]);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    if (deviceId) {
      fetchPendingNotifications();
      // Poll every 60 seconds for new notifications
      const interval = setInterval(fetchPendingNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [deviceId, fetchPendingNotifications]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !vapidPublicKey || !deviceId) {
      return { success: false, error: 'Push notifications not supported' };
    }

    setIsLoading(true);
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setIsLoading(false);
        return { success: false, error: 'Permission denied' };
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      await axios.post(`${API}/notifications/subscribe`, {
        device_id: deviceId,
        endpoint: subscriptionJson.endpoint,
        p256dh: subscriptionJson.keys.p256dh,
        auth: subscriptionJson.keys.auth
      });

      setIsSubscribed(true);
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      logger.error('Failed to subscribe:', error);
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  }, [isSupported, vapidPublicKey, deviceId]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!deviceId) return;

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      await axios.delete(`${API}/notifications/unsubscribe/${deviceId}`);
      setIsSubscribed(false);
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      logger.error('Failed to unsubscribe:', error);
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  }, [deviceId]);

  // Mark notifications as read
  const markAsRead = useCallback(async (notificationIds = null) => {
    if (!deviceId) return;

    try {
      await axios.post(`${API}/notifications/mark-read`, {
        device_id: deviceId,
        notification_ids: notificationIds
      });
      
      // Refresh notifications
      await fetchPendingNotifications();
    } catch (error) {
      logger.error('Failed to mark as read:', error);
    }
  }, [deviceId, fetchPendingNotifications]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    pendingNotifications,
    allNotifications,
    notificationCount,
    markAsRead,
    fetchPendingNotifications
  };
}
